import React, { useState, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { Link, useLocation } from "react-router-dom";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Popover from "@mui/material/Popover";
import Links from "./Links";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material";

interface AdminSidemenuProps {
  open: boolean;
}

const AdminSidemenu: React.FC<AdminSidemenuProps> = ({ open }) => {
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContent, setPopoverContent] = useState<any>(null);
  const useInfo = useSelector((state: any) => state.AuthSlice.adminAuthUser);
  const [menuLinks, setMenuLinks] = useState(Links);
  const theme = useTheme();

  /*
   * Which permission a menu item needs.
   *
   * ONE RULE: a sub-item inherits its group's permission unless it says
   * otherwise. Tick "Orders" on a role and everything under Orders &
   * Refunds opens -- Orders, Refunds, Bank Transfers -- which is what
   * someone granting "order management" already assumes.
   *
   * It was not that way, and it cost two bugs in one afternoon. Every
   * sub-item carried its own key, worked out by matching substrings
   * against the URL, and a name that matched nothing came back empty:
   * /bank-transfers, /payment-failures, /coupon-code and /service-fee all
   * did. /adminSettings matched `includes("settings")` and resolved to
   * "cms", with the line meant to catch it sitting below, unreachable.
   * Refunds asked outright for a permission called "refunds", which is
   * not one a role can hold at all.
   *
   * None of it showed, because the renderer ignored permissions on
   * sub-items entirely -- every wrong answer was hidden by a second
   * fault. Fixing that one made all of them visible at once.
   *
   * Order of precedence: what the item declares, then its group, then the
   * URL guesses kept below for sub-pages that are not in the menu at all.
   * An item only needs a key of its own when it genuinely differs from
   * its group, and today none does.
   */
  const getPermissionKey = (item: any, parent?: any): string => {
    if (item?.permissionKey) return item.permissionKey;
    if (parent?.permissionKey) return parent.permissionKey;

    const path = item?.link || "";
    if (path.includes("inventory")) return "inventories";
    if (path.includes("report")) return "reports";
    if (path.includes("product")) return "products";
    if (path.includes("categories")) return "categories";
    if (path.includes("employee")) return "employee";
    if (path.includes("roles")) return "roles";
    if (path.includes("customer-help-service")) return "customer-help-service";
    if (path.includes("customers")) return "customers";
    if (path.includes("supplier")) return "supplier";
    if (path.includes("orders")) return "orders";
    if (path.includes("refunds")) return "orders";
    if (path.includes("home-screen")) return "home-screen";
    if (path.includes("taxation")) return "profile";
    if (path.includes("dashboard")) return "dashboard";

    return "";
  };

  const filterMenuLinks = (links: any[], permissions: any) => {
    return links
      .map((item: any) => {
        const key = getPermissionKey(item); // item.permissionKey or from path


        const hasSub = Array.isArray(item.subMenu) && item.subMenu.length > 0;

        if (hasSub) {
          if (key && permissions?.[key]?.view === true) return item;

          // OR if any submenu has valid permission key with view === true
          const hasSubPermission = item.subMenu.some((sub: any) => {
            const subKey = getPermissionKey(sub, item);
            return subKey && permissions?.[subKey]?.view === true;
          });

          if (hasSubPermission) {
            return item;
          }

          return null;
        }

        if (key && permissions?.[key]?.view === true) {
          return item;
        }

        return null;
      })
      .filter(Boolean);
  };

  // Give Permission Based on Role
  useEffect(() => {
    if (useInfo?.permissions && Object.keys(useInfo.permissions).length) {
      const updatedLinks = menuLinks.map((item: any) => {
        const key = getPermissionKey(item); // Extract key from link

        let hasPermission = key && useInfo.permissions[key]?.view;
        return {
          ...item,
          isActive:
            // "/" and "#" are structural, not modules. Dashboard is a module
            // like any other and is filtered on its own permission.
            item.link === "/" || item.link === "#" ? true : !!hasPermission,
          subMenu: item.subMenu?.map((sub: any) => {
            const subKey = getPermissionKey(sub, item);
            return {
              ...sub,
              isActive: !!(subKey && useInfo.permissions[subKey]?.view),
            };
          }),
        };
      });
      setMenuLinks(updatedLinks);
    }
  }, [useInfo]);

  // Initialize openSubmenu based on current path
  React.useEffect(() => {
    Links.forEach((item: any) => {
      if (item.subMenu) {
        const isSubMenuActive = item.subMenu.some(
          (subItem: any) => subItem.link === location.pathname,
        );
        if (isSubMenuActive) {
          setOpenSubmenu(item.name);
        }
      }
    });
  }, [location.pathname]);

  const isLinkActive = (link: string) => {
    if (link === "#") return false;
    return location.pathname === link;
  };

  const isParentActive = (item: any) => {
    if (!item.subMenu) return false;
    return item.subMenu.some(
      (subItem: any) => location.pathname === subItem.link,
    );
  };

  const toggleSubmenu = (submenu: string) => {
    setOpenSubmenu((prev) => (prev === submenu ? null : submenu));
  };

  const handlePopoverToggle = (
    event: React.MouseEvent<HTMLElement>,
    item: any,
  ) => {
    if (!open && item.subMenu) {
      if (anchorEl) {
        handlePopoverClose();
      } else {
        setAnchorEl(event.currentTarget);
        setPopoverContent(item.subMenu);
      }
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverContent(null);
  };

  const popoverOpen = Boolean(anchorEl);

  const filteredMenuLinks = filterMenuLinks(menuLinks, useInfo.permissions);

  return (
    <List>
      {filteredMenuLinks.map((item: any) =>
        item.isActive ? (
          <React.Fragment key={item.name}>
            <ListItem disablePadding sx={{ display: "block" }} component="li">
              <ListItemButton
                component={Link}
                to={item.link}
                onClick={(e) =>
                  open
                    ? item.subMenu
                      ? toggleSubmenu(item.name)
                      : null
                    : handlePopoverToggle(e, item)
                }
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  backgroundColor:
                    isLinkActive(item.link) || (!open && isParentActive(item))
                      ? theme.palette.primary.main
                      : "transparent",
                  color:
                    isLinkActive(item.link) || (!open && isParentActive(item))
                      ? "#fff"
                      : "black",
                  transition: "background-color 0.3s ease, color 0.3s ease",
                  "& .MuiListItemText-primary": {
                    color:
                      isLinkActive(item.link) || (!open && isParentActive(item))
                        ? "#fff"
                        : "black", // ✅ text
                  },
                  "& .MuiListItemIcon-root": {
                    color:
                      isLinkActive(item.link) || (!open && isParentActive(item))
                        ? "#fff"
                        : theme.palette.primary.main, // ✅ icon
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                    "& .MuiListItemText-primary": {
                      color: "#fff",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "#fff",
                    },
                  },
                }}
              >
                {open && isParentActive(item) && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      marginLeft: "auto",
                      // marginRight: 2,
                    }}
                  />
                )}
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 1 : "auto",
                    justifyContent: "center",
                    color:
                      isLinkActive(item.link) || (!open && isParentActive(item))
                        ? "#fff"
                        : theme.palette.primary.main,
                    transition: "color 0.3s ease",
                    fontSize: "1.5rem",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    marginLeft: 2,
                  }}
                >
                  {React.cloneElement(item.icon, { size: "1.25rem" })}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.name}
                    sx={{
                      opacity: 1,
                      color: theme.palette.primary.main,
                      ml: 0,
                    }}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: "0.875rem",
                        color:
                          isLinkActive(item.link) || isParentActive(item)
                            ? "#fff"
                            : theme.palette.primary.main,
                      },
                    }}
                  />
                )}
                {open &&
                  item.subMenu &&
                  (openSubmenu === item.name ? (
                    <FaChevronUp
                      style={{
                        color:
                          isLinkActive(item.link) || isParentActive(item)
                            ? "#fff"
                            : theme.palette.primary.main,
                        transition: "color 0.3s ease",
                      }}
                    />
                  ) : (
                    <FaChevronDown
                      style={{
                        color:
                          isLinkActive(item.link) || isParentActive(item)
                            ? "#fff"
                            : theme.palette.primary.main,
                        transition: "color 0.3s ease",
                      }}
                    />
                  ))}
              </ListItemButton>
            </ListItem>

            {open && item.subMenu && (
              <Collapse
                in={openSubmenu === item.name}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {/*
                    Children are filtered too.
                    
                    isActive was being computed for every submenu item from
                    its own permission, and then ignored here -- only the
                    top-level loop above checked it. So anyone who could see
                    a GROUP saw every item inside it. That is how a role
                    with orders but not settings was shown Bank Transfers
                    and then refused by the page.
                  */}
                  {item.subMenu
                    .filter((subItem: any) => subItem.isActive !== false)
                    .map((subItem: any) => (
                    <ListItem
                      sx={{ pl: 3, py: 0 }}
                      key={subItem.name}
                      component="li"
                    >
                      <ListItemButton
                        component={Link}
                        to={subItem.link}
                        sx={{
                          height: 40,

                          backgroundColor: isLinkActive(subItem.link)
                            ? theme.palette.primary.main
                            : "transparent",
                          color: isLinkActive(subItem.link) ? "#fff" : "black",
                          "& .MuiListItemText-primary": {
                            color: isLinkActive(subItem.link)
                              ? "#fff"
                              : "black", // ✅ text
                          },
                          "& .MuiListItemIcon-root": {
                            color: isLinkActive(subItem.link)
                              ? "#fff"
                              : theme.palette.primary.main, // ✅ icon
                          },
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            "& .MuiListItemText-primary": {
                              color: "#fff",
                            },
                            "& .MuiListItemIcon-root": {
                              color: "#fff",
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 0.5,
                            fontSize: "1.5rem",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            // color: isLinkActive(subItem.link)
                            //   ? "#fff"
                            //   : theme.palette.primary.main,
                            color: isLinkActive(subItem.link)
                              ? "#fff"
                              : theme.palette.primary.main,
                            marginLeft: 2,
                          }}
                        >
                          {React.cloneElement(subItem.icon, { size: "1rem" })}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.name}
                          // sx={{ color: theme.palette.primary.main }}
                          sx={{
                            color: isLinkActive(subItem.link)
                              ? "#fff"
                              : theme.palette.primary.main,
                          }}
                          primaryTypographyProps={{
                            sx: { fontSize: "0.8425rem" },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ) : null,
      )}

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        disableRestoreFocus
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          style: { padding: 0 },
        }}
      >
        <List disablePadding>
          {popoverContent &&
            popoverContent.map((subItem: any) => (
              <ListItem key={subItem.name} component="li" disablePadding>
                <ListItemButton
                  component={Link}
                  to={subItem.link}
                  sx={{
                    height: 40,
                    backgroundColor: isLinkActive(subItem.link)
                      ? theme.palette.primary.main
                      : "transparent",
                    color: isLinkActive(subItem.link)
                      ? "#fff"
                      : theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 1,
                      color: "inherit",
                      fontSize: "1.5rem",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {React.cloneElement(subItem.icon, { size: "1rem" })}
                  </ListItemIcon>
                  <ListItemText
                    primary={subItem.name}
                    primaryTypographyProps={{
                      sx: { fontSize: "0.8125rem" },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Popover>
    </List>
  );
};

export default AdminSidemenu;
